/**
 * Un scénario = un tracé + une liste d'index de gares + des réglages.
 * Ce module produit tout ce que l'interface affiche : temps bout en bout,
 * vitesse commerciale, horaires, temps inter-villes, coût marginal d'une gare.
 */
import { speedProfile, type ProfileResult } from "./profile";
import { STOCK, jerkPenalty, type RollingStock, type StockId } from "./rollingstock";
import type { RouteData } from "./route";

/** Paramètres d'un matériel saisi à la main. */
export interface PersoParams {
  mass_kg: number;
  power_w: number;
  a0: number;
  d: number;
  vmax_ms: number;
}

/** Bornes de saisie du matériel personnalisé (SI). */
export const PERSO_BOUNDS: Record<keyof PersoParams, [number, number]> = {
  mass_kg: [20e3, 400e3],
  power_w: [200e3, 10e6],
  a0: [0.3, 2.0],
  d: [0.3, 2.5],
  vmax_ms: [40 / 3.6, 200 / 3.6],
};

export interface RunSettings {
  stock: StockId | "perso";
  /** paramètres du matériel personnalisé, utilisés si stock === "perso" */
  perso?: PersoParams;
  /** temps d'arrêt en gare, s */
  tarr: number;
  /** appliquer les limitations de vitesse par section (courbes, surcharges) */
  useVmax: boolean;
  /** appliquer le profil de pente */
  useGrade: boolean;
}

export const DEFAULT_SETTINGS: RunSettings = {
  stock: "flirt",
  tarr: 30,
  useVmax: true,
  useGrade: true,
};

export function clampPerso(p: PersoParams): PersoParams {
  const out = { ...p };
  for (const k of Object.keys(PERSO_BOUNDS) as (keyof PersoParams)[]) {
    const [lo, hi] = PERSO_BOUNDS[k];
    const v = Number(out[k]);
    out[k] = Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : (lo + hi) / 2;
  }
  return out;
}

/** Le matériel effectif d'un réglage : un preset, ou les paramètres saisis. */
export function stockFor(settings: RunSettings): RollingStock {
  if (settings.stock === "perso") {
    const p = clampPerso(settings.perso ?? STOCK.flirt);
    return { id: "flirt", ...p, label: "Matériel personnalisé", family: "régional" };
  }
  return STOCK[settings.stock];
}

export interface Leg {
  from: number;
  to: number;
  /** temps de marche entre les deux arrêts, s */
  t_s: number;
  length_m: number;
}

export interface RunResult extends ProfileResult {
  stock: RollingStock;
  /** temps de séjour à chaque arrêt intermédiaire (arrêt + jerk), s */
  dwell_s: number;
  length_m: number;
  vc_kmh: number;
  legs: Leg[];
  /** heure d'arrivée à chaque arrêt (index parallèle à stops), s depuis le départ */
  arrivals: number[];
  /** heure de départ de chaque arrêt, s depuis le départ */
  departures: number[];
}

/** Plafond de vitesse par segment (m/s) : min(limitation de section, vmax du matériel). */
export function vmaxProfileFor(route: RouteData, stock: RollingStock, useVmax: boolean): Float64Array | null {
  if (!useVmax) return null;
  const n = route.coords.length - 1;
  const cap = new Float64Array(n);
  for (let x = 0; x < n; x++) {
    cap[x] = Math.min(route.vmax_kmh[x] / 3.6, stock.vmax_ms);
  }
  return cap;
}

const ZERO_CACHE = new Map<number, Float64Array>();
function zeros(n: number): Float64Array {
  let z = ZERO_CACHE.get(n);
  if (!z) {
    z = new Float64Array(n);
    ZERO_CACHE.set(n, z);
  }
  return z;
}

export function runScenario(route: RouteData, stopsIdx: ArrayLike<number>, settings: RunSettings): RunResult {
  const stock = stockFor(settings);
  const grade = settings.useGrade ? route.grade : zeros(route.grade.length);
  const prof = speedProfile(stopsIdx, grade, route.step_m, stock, {
    vmaxProfile: vmaxProfileFor(route, stock, settings.useVmax),
    tarr: settings.tarr,
  });
  const dwell = settings.tarr + jerkPenalty(stock);
  const { stops, tCum } = prof;
  const legs: Leg[] = [];
  const arrivals: number[] = [];
  const departures: number[] = [];
  let clock = 0;
  for (let k = 0; k < stops.length; k++) {
    if (k > 0) {
      const t = tCum[stops[k]] - tCum[stops[k - 1]];
      legs.push({ from: stops[k - 1], to: stops[k], t_s: t, length_m: (stops[k] - stops[k - 1]) * route.step_m });
      clock += t;
    }
    arrivals.push(clock);
    if (k > 0 && k < stops.length - 1) clock += dwell;
    departures.push(clock);
  }
  const length_m = stops.length >= 2 ? (stops[stops.length - 1] - stops[0]) * route.step_m : 0;
  return {
    ...prof,
    stock,
    dwell_s: dwell,
    length_m,
    vc_kmh: prof.T > 0 ? (length_m / prof.T) * 3.6 : 0,
    legs,
    arrivals,
    departures,
  };
}

/** Temps de parcours (s) entre deux points de la ligne, départ de a → arrivée en b.
 *  a et b sont des index de grille quelconques (pas forcément des gares). */
export function travelTime(res: RunResult, a: number, b: number): number {
  if (a > b) [a, b] = [b, a];
  const { stops, tCum, dwell_s } = res;
  let t = tCum[b] - tCum[a];
  // arrêts intermédiaires strictement entre a et b (a et b eux-mêmes ne comptent pas)
  for (let k = 1; k < stops.length - 1; k++) {
    if (stops[k] > a && stops[k] < b) t += dwell_s;
  }
  return t;
}

/** Coût marginal d'une gare : T(avec) − T(sans), en s. */
export function stationCost(route: RouteData, stops: number[], idx: number, settings: RunSettings): number {
  const withIt = runScenario(route, stops, settings).T;
  const without = runScenario(
    route,
    stops.filter((s) => s !== idx),
    settings,
  ).T;
  return withIt - without;
}

/** Format « 1 h 34 » / « 4 min 07 ». */
export function fmtDuration(s: number): string {
  const t = Math.round(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  if (h > 0) return `${h} h ${String(m).padStart(2, "0")}`;
  return `${m} min ${String(sec).padStart(2, "0")}`;
}
